/** @jsxImportSource @emotion/react */
import {
  Box,
  Button,
  Divider,
  Heading,
  Icon,
  Input,
  Logo,
  LogoVariant,
  Text,
} from '@landtrustinc/design-system';
import React from 'react';

import type { BuckChatConfig, OtpContext } from '../../../types';
import { useMethodStep } from '../hooks/useMethodStep';
import type { AnalyticsHandler } from '../hooks/useSignupFlow';
import { facebookButtonStyles, helperTextStyles } from './styles';

type MethodStepProps = {
  config: BuckChatConfig;
  context?: OtpContext;
  onCodeSent: (email: string) => void;
  onAnalyticsEvent?: AnalyticsHandler;
  handleGoogleAuth: () => void;
  handleFacebookAuth: () => void;
  header?: string;
  subtext?: string;
  logoVariant?: LogoVariant;
};

const DEFAULT_HEADER = 'Create your free LandTrust account.';
const DEFAULT_SUBTEXT =
  "You're one step away from saving this package and getting more help from Buck.";

export const MethodStep: React.FC<MethodStepProps> = ({
  config,
  context,
  onCodeSent,
  onAnalyticsEvent,
  handleGoogleAuth,
  handleFacebookAuth,
  header = DEFAULT_HEADER,
  subtext = DEFAULT_SUBTEXT,
  logoVariant,
}) => {
  const { email, setEmail, emailError, sendingCode, handleSendCode } = useMethodStep({
    apiBaseUrl: config.apiBaseUrl ?? '',
    context,
    onCodeSent,
    onAnalyticsEvent,
  });

  return (
    <Box display="flex" flexDirection="column" gap="var(--spacing-3)">
      {logoVariant && (
        <Box display="flex" justifyContent="center">
          <Logo variant={logoVariant} theme="light" size={48} />
        </Box>
      )}
      <Heading size="xs" fontWeight="extrabold" textAlign="center" textWrap="wrap">
        {header}
      </Heading>
      {subtext ? (
        <Box mb="var(--spacing-3)">
          <Text size="md" textAlign="center">
            {subtext}
          </Text>
        </Box>
      ) : null}

      {/* Social Auth Buttons */}
      {config.googleAuthUrl && (
        <Button
          variant="secondary"
          fullWidth
          icon={<Icon variant="Google" />}
          onClick={handleGoogleAuth}
        >
          Continue with Google
        </Button>
      )}

      {config.facebookAuthUrl && (
        <Button
          variant="tertiary"
          fullWidth
          icon={<Icon variant="Facebook" />}
          onClick={handleFacebookAuth}
          css={facebookButtonStyles}
        >
          Continue with Facebook
        </Button>
      )}

      <Box display="flex" gap="var(--spacing-4)" alignItems="center" my="var(--spacing-2)">
        <Divider />
        <Text size="md" color="var(--text-secondary)">
          OR
        </Text>
        <Divider />
      </Box>

      {/* Email Input */}
      <Box display="flex" flexDirection="column" gap="var(--spacing-1)" mb="var(--spacing-3)">
        <Text size="md" fontWeight="bold">
          Email
        </Text>
        <Input
          placeholder="you@example.com"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailError}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && email && !sendingCode) {
              handleSendCode();
            }
          }}
        />
        {!emailError ? (
          <Text size="xs" color="var(--text-secondary)" css={helperTextStyles}>
            We&apos;ll send you a code to verify your email.
          </Text>
        ) : (
          <Text size="xs" color="var(--text-error)" css={helperTextStyles}>
            {emailError}
          </Text>
        )}
      </Box>
      <Button
        variant="primary"
        isLoading={sendingCode}
        fullWidth
        onClick={handleSendCode}
        disabled={!email || sendingCode}
      >
        Continue with Email
      </Button>
    </Box>
  );
};
